import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import TextSnippetOutlined from "@mui/icons-material/TextSnippetOutlined";
import type { CardHeaderProps } from "@mui/material";
import { Popper } from "@mui/material";
import type { FieldProps } from "formik";
import { Field } from "formik";
import type { ReactNode } from "react";
import { useReducer, useRef } from "react";
import { FormattedMessage } from "react-intl";

import { IconButton } from "@citrine/atoms/icon-button";

import { ResetButton, SubmitButton } from "./buttons";
import { Form } from "./form";
import * as sx from "./popper-text-field.sx";

const CARD_HEADER_SLOT_PROPS = {
  title: {
    variant: "h4",
  },
} as const satisfies CardHeaderProps["slotProps"];

type DefaultValue = string | undefined;

interface State {
  view: "closed" | "viewing" | "editing";
}

type Action<Value extends DefaultValue = DefaultValue> =
  | {
      type: "open";
      value: Value;
    }
  | {
      type: "close";
    }
  | {
      type: "edit";
    }
  | {
      type: "reset";
      value: Value;
    }
  | {
      type: "save";
      value: Value;
    };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "open":
      return {
        ...state,
        view: action.value ? "viewing" : "editing",
      };
    case "close":
      return {
        ...state,
        view: "closed",
      };
    case "edit":
      return {
        ...state,
        view: "editing",
      };
    case "reset":
      return {
        ...state,
        view: action.value ? "viewing" : "closed",
      };
    case "save":
      return {
        ...state,
        view: action.value ? "viewing" : "closed",
      };
  }
}

export interface PopperTextFieldProps<
  Value extends DefaultValue = DefaultValue,
> {
  defaultValue?: Value;
  name?: string;
  readonly?: boolean;
  title?: ReactNode;
  validate?: (value: Value) => string | undefined | Promise<string | undefined>;
  value?: Value;
  onSubmit: (value: string) => unknown;
}

export function PopperTextField<Value extends DefaultValue = DefaultValue>({
  defaultValue,
  validate,
  name = "value",
  readonly,
  title,
  value,
  onSubmit,
}: PopperTextFieldProps<Value>) {
  const initialValue = value ?? defaultValue ?? ("" as Value);

  const [state, dispatch] = useReducer(reducer, {
    view: "closed",
  });

  const view = readonly && state.view === "editing" ? "viewing" : state.view;

  const anchorEl = useRef(null);

  return readonly && !initialValue ? null : (
    <>
      <IconButton
        ref={anchorEl}
        tooltip={title}
        onClick={() =>
          dispatch({
            type: view === "closed" ? "open" : "close",
            value: initialValue,
          })
        }
      >
        <TextSnippetOutlined />
      </IconButton>

      <Popper open={view !== "closed"} anchorEl={anchorEl.current}>
        <Form<Record<string, Value>>
          enableReinitialize={value !== undefined && view !== "editing"}
          initialValues={{ [name]: initialValue }}
          onReset={() => dispatch({ type: "reset", value: initialValue })}
          onSubmit={async (values) => {
            const newValue = values[name]!;
            await onSubmit(newValue);
            dispatch({ type: "save", value: newValue });
          }}
          validate={async (values) => {
            const validateValue = values[name];
            const error = await validate?.(validateValue);
            return error ? { [name]: error } : undefined;
          }}
        >
          <sx.Card elevation={4}>
            <sx.CardHeader
              title={title}
              slotProps={CARD_HEADER_SLOT_PROPS}
              action={
                <>
                  {view === "editing" || readonly ? null : (
                    <IconButton
                      size="small"
                      tooltip={<FormattedMessage id="Edit" />}
                      onClick={() => dispatch({ type: "edit" })}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    tooltip={<FormattedMessage id="close" />}
                    type="reset"
                    onClick={() => dispatch({ type: "close" })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              }
            />
            <sx.CardContent>
              {view === "editing" ? (
                <sx.TextField
                  autoComplete="off"
                  autoFocus
                  fullWidth
                  multiline
                  minRows={3}
                  name={name}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      dispatch({ type: "reset", value: initialValue });
                    }
                  }}
                />
              ) : (
                <Field name={name}>
                  {({ field }: FieldProps) => field.value}
                </Field>
              )}
            </sx.CardContent>

            {view === "editing" ? (
              <sx.CardActions>
                <ResetButton size="medium">
                  <FormattedMessage id="Cancel" />
                </ResetButton>

                <SubmitButton size="medium">
                  <FormattedMessage id="save" />
                </SubmitButton>
              </sx.CardActions>
            ) : null}
          </sx.Card>
        </Form>
      </Popper>
    </>
  );
}
